import { CheckCircle, Eye, EyeSlash, X } from "@phosphor-icons/react";
import { passwordChecklist } from "./passwordChecklist";

/**
 * A password field with its show and hide button, shared by the sign-in form and the screen that
 * sets a password. The button's names, "Visa lösenord" and "Dölj lösenord", and `aria-pressed` are
 * the ones both screens had before the second build round.
 */
export function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  revealed,
  onToggle,
  name,
  disabled,
  minLength,
  describedBy
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: "current-password" | "new-password";
  revealed: boolean;
  onToggle: () => void;
  name?: string;
  disabled?: boolean;
  minLength?: number;
  describedBy?: string;
}) {
  return (
    <div className="box-field">
      <label htmlFor={id}>{label}</label>
      <div className="k-password">
        <input
          id={id}
          name={name}
          type={revealed ? "text" : "password"}
          autoComplete={autoComplete}
          required
          minLength={minLength}
          value={value}
          disabled={disabled}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="icon-button"
          aria-label={revealed ? "Dölj lösenord" : "Visa lösenord"}
          aria-pressed={revealed}
          onClick={onToggle}
        >
          {revealed ? <EyeSlash size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}

/**
 * The rules, ticking as the person types. Each line says in words whether it is met, so the tick's
 * colour is never the only signal (design.md section 3), and the list describes the field it sits
 * under, so a screen reader hears it with the field rather than finding it later.
 */
export function PasswordChecklist({ id, password }: { id: string; password: string }) {
  return (
    <ul className="k-rules" id={id} aria-label="Krav på lösenordet" data-testid="password-rules">
      {passwordChecklist(password).map((rule) => (
        <li key={rule.key} className={rule.met ? "is-ok" : undefined} data-met={rule.met}>
          {rule.met ? <CheckCircle size={16} aria-hidden="true" /> : <X size={16} aria-hidden="true" />}
          <span>{rule.label}</span>
          <span className="visually-hidden">{rule.met ? ", klart" : ", saknas"}</span>
        </li>
      ))}
    </ul>
  );
}
